import { Provider } from 'react-redux'

import { AccordionStateProvider } from '@app/contexts/AccordionStateContext/AccordionStateContext'
import { LayerProvider } from '@app/contexts/LayerContext'
import { LayerDetailsProvider } from '@app/contexts/LayerDetailsContext'
import { PrioritisationContextProvider } from '@app/contexts/ModuleContexts/PrioritisationContext'
import { PrioritisationMapProvider } from '@app/contexts/ModuleMapContexts/PrioritisationMapContext'
import { PropertyMapProvider } from '@app/contexts/ModuleMapContexts/PropertyMapContext/PropertyMapContext'
import { MapProvider } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { RiskFetcherProvider } from '@app/contexts/RiskContext'
import { SnackbarProvider } from '@app/contexts/SnackbarContext'
import { TutorialContextProvider } from '@app/contexts/TutorialContext'
import { ToastProvider } from './components/ui/Toast/ToastProvider'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LiveMapProvider } from '@app/modules/Live/contexts/Live.MapContext'
import store from '@app/redux/store'

interface AppProvidersProps {
  children: React.ReactNode
}

const compose = (...providers: Array<React.ComponentType<AppProvidersProps>>) => {
  return providers.reduce(
    (AccumulatedProviders, CurrentProvider) => {
      const ComposedComponent = ({ children }: AppProvidersProps) => (
        <AccumulatedProviders>
          <CurrentProvider>{children}</CurrentProvider>
        </AccumulatedProviders>
      )
      ComposedComponent.displayName = `Composed(${
        CurrentProvider.displayName || CurrentProvider.name || 'Component'
      })`

      return ComposedComponent
    },
    ({ children }: AppProvidersProps) => <>{children}</>,
  )
}

export const AppProviders = compose(
  // Redux store for state management
  ({ children }) => <Provider store={store}>{children}</Provider>,

  //   ui providers
  AccordionStateProvider,
  SnackbarProvider,
  ToastProvider,

  TutorialContextProvider,
  // mui provider for date pickers
  ({ children }) => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>{children}</LocalizationProvider>
  ),

  //   map providers
  MapProvider,
  LayerProvider,
  LayerDetailsProvider,

  RiskFetcherProvider,

  PrioritisationMapProvider,
  PrioritisationContextProvider,
  PropertyMapProvider,
  LiveMapProvider,
)
